import { useSelfHiringContext } from "@/app/contexts/SelfHiringContext";
import { useSelfHiringMutation } from "@/app/hooks/useSelfHiringMutation";
import { selfHireService } from "@/app/services/selfHireService";
import { AttachmentType } from "@/app/services/transactionService/attachmentsType";
import { useToast } from "@/view/components/ui/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const listDocumentsSchema = z.object({});

type ListDocumentsFormData = z.infer<typeof listDocumentsSchema>;

export type UploadedFileSource = "gallery" | "camera";

export interface UploadedFile {
  id: string;
  file: File;
  preview?: string;
  side?: "frente" | "verso";
  source: UploadedFileSource;
}

export interface DocumentType {
  id: string;
  title: string;
  description?: string;
  files: UploadedFile[];
  required: boolean;
  maxFiles: number;
  isIdDocument: boolean;
  isEnergyBill: boolean;
}


export const useDocuments = () => {
  const { nextStep, crefazData } = useSelfHiringContext();
  const { toast } = useToast();
  const transactionId = crefazData?.transactionId;
  const MAX_FILE_SIZE_BYTES = 4 * 1024 * 1024; // 4MB

  const { uploadAttachmentCrefazMutation } = useSelfHiringMutation();

  const form = useForm<ListDocumentsFormData>({
    resolver: zodResolver(listDocumentsSchema),
  });

  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState<DocumentType[]>([]);

  const { data: attachmentsType, isPending: isLoadingAttachmentsType } = useQuery<AttachmentType[]>({
    queryKey: ["attachments-type", transactionId],
    queryFn: () => selfHireService.getAttachmentCrefaz(transactionId!),
    enabled: !!transactionId,
  });

  const handleAddFile = (
    documentId: string,
    file: File,
    side?: "frente" | "verso",
    source: UploadedFileSource = "gallery",
  ) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast({
        title: "O tamanho máximo permitido é 4 MB.",
        variant: "destructive",
      });
      return;
    }

    const newFile: UploadedFile = {
      id: crypto.randomUUID(),
      file,
      preview: file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : undefined,
      side,
      source,
    };

    setDocuments((prev) =>
      prev.map((doc) => {
        if (doc.id !== documentId) return doc;

        if (side) {
          doc.files
            .filter((f) => f.side === side)
            .forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
          const remaining = doc.files.filter((f) => f.side !== side);
          return { ...doc, files: [...remaining, newFile] };
        }

        doc.files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
        return { ...doc, files: [newFile] };
      })
    );
  };

  const submitDocuments = form.handleSubmit(async () => {
    const missingRequired = documents.some(
      (doc) => doc.required && doc.files.length === 0
    );

    if (missingRequired) {
      toast({
        title: "Envie todos os documentos necessários",
        variant: "destructive",
      });
      return;
    }

    if (transactionId) {
      setIsLoading(true);
      try {
        for (const doc of documents) {
          for (const file of doc.files) {
            await uploadAttachmentCrefazMutation.mutateAsync({
              id: transactionId!,
              documentoId: doc.id,
              file: file.file
            });
          }
        }
        nextStep();
      } catch (error) {
        console.error("Error uploading files", error);
        toast({
          title: "Erro ao enviar documentos",
          description: "Tente novamente em instantes.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    }

  });

  useEffect(() => {
    if (attachmentsType) {
      setDocuments(() => {
        return attachmentsType.map((att) => {
          const isId = att.nome.toUpperCase() === "DOCUMENTO DE IDENTIFICAÇÃO";
          const isEnergyBill = att.nome.toUpperCase() === "FATURA DE ENERGIA";

          return {
            id: att.documentId.toString(),
            title: att.nome,
            description: isId ? "RG, CNH ou Passaporte" : "Selecione um documento",
            files: [],
            required: true,
            maxFiles: isId ? 2 : 1,
            isIdDocument: isId,
            isEnergyBill: isEnergyBill,
          };
        });
      });
    } else if (!transactionId) {
      setDocuments([
        {
          id: "energy_bill",
          title: "Fatura de Energia",
          description: "Selecione um documento",
          files: [],
          required: true,
          maxFiles: 1,
          isIdDocument: false,
          isEnergyBill: true,
        },
        {
          id: "id_document",
          title: "Documento de identificação",
          description: "RG, CNH ou Passaporte",
          files: [],
          required: true,
          maxFiles: 2,
          isIdDocument: true,
          isEnergyBill: false,
        },
      ]);
    }
  }, [attachmentsType, transactionId]);


  return {
    isLoadingAttachmentsType,
    documents,
    handleAddFile,
    isLoading,
    submitDocuments

  }
}